<script setup lang="ts">
/* =============================================================
   VESSEL — CREATE / EDIT
   =============================================================
   One page, two jobs, told apart by whether the route carries an
   id — the sibling of BerthFormPage and CompanyFormPage, and the
   notes there about why create and edit share a component, and
   why this is a page rather than a dialog, apply here too.

   docs/data-model.md gives a vessel a flag and a set of service
   needs as well. Neither column exists yet, so neither is on the
   form: see the head of
   supabase/migrations/20260919090628_create_vessels.sql.
   ============================================================= */

import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useUnsavedGuard } from '@/lib/useUnsavedGuard'
import PageHeader from '@/components/ui/PageHeader.vue'
import Panel from '@/components/ui/Panel.vue'
import Button from '@/components/ui/Button.vue'
import TextField from '@/components/ui/TextField.vue'
import SelectField from '@/components/ui/SelectField.vue'
import Alert from '@/components/ui/Alert.vue'
import { routes, to } from '@/config/routes'
import { createVessel, getVessel, updateVessel } from '@/lib/vessels'
import { listCompanies } from '@/lib/companies'
import { useVesselTypes } from '@/lib/vesselTypes'
import { DataError } from '@/lib/errors'
import type { Company, VesselInput, VesselType } from '@/lib/types'

const route = useRoute()
const router = useRouter()
const { vesselTypes } = useVesselTypes()

const editingId = computed(() => (route.params.id ? Number(route.params.id) : null))
const isEdit = computed(() => editingId.value !== null)

interface FormState {
  name: string
  /** The <select> value, so '' means nothing chosen. Parsed on submit. */
  company_id: string
  /** Likewise — '' until a type is picked, which is not a VesselType. */
  type: string
  length_m: string
  width_m: string
  draft_m: string
}

const blank: FormState = {
  name: '',
  company_id: '',
  type: '',
  length_m: '',
  width_m: '',
  draft_m: '',
}

const form = ref<FormState>({ ...blank })
/** What was loaded, to tell a real edit from a visit. */
const pristine = ref<FormState>({ ...blank })
const heading = ref('New vessel')

const loading = ref(false)
const saving = ref(false)
const notFound = ref(false)
const formError = ref('')
const errors = ref<Record<string, string>>({})

/*
 * The operator list, fetched per visit rather than cached — the same call
 * BerthFormPage makes, and for the same reason: companies are created and
 * renamed in this panel, so a module-level copy is stale exactly when someone
 * has just added the company they came here to attach a ship to.
 */
const companies = ref<Company[]>([])
const companiesFailed = ref(false)

async function loadCompanies(): Promise<void> {
  try {
    companies.value = await listCompanies()
    companiesFailed.value = false
  } catch {
    companiesFailed.value = true
  }
}

/*
 * `vessels.company_id` is NOT NULL, so with no company on the platform there
 * is nothing valid to submit. Saying so beats an empty dropdown and a foreign
 * key error.
 */
const noCompanies = computed(() => !companiesFailed.value && companies.value.length === 0)

const dirty = computed(() => JSON.stringify(form.value) !== JSON.stringify(pristine.value))

onMounted(async () => {
  if (!isEdit.value) {
    await loadCompanies()
    return
  }
  loading.value = true
  try {
    // Both at once: neither needs the other, and the form cannot be used
    // until both have arrived.
    const [vessel] = await Promise.all([getVessel(editingId.value as number), loadCompanies()])
    if (!vessel) {
      notFound.value = true
      return
    }
    const state: FormState = {
      name: vessel.name,
      company_id: String(vessel.company_id),
      type: vessel.type,
      length_m: String(vessel.length_m),
      width_m: String(vessel.width_m),
      draft_m: String(vessel.draft_m),
    }
    form.value = { ...state }
    pristine.value = { ...state }
    heading.value = vessel.name
  } catch (e) {
    formError.value = e instanceof DataError ? e.message : 'Could not load the vessel.'
  } finally {
    loading.value = false
  }
})

/**
 * One dimension, in metres. Required — all three columns are NOT NULL — and
 * positive, which is the CHECK. The ceiling is the column's own precision
 * rather than naval architecture: numeric(6,2) cannot hold 10000, and a
 * client-side message beats Postgres's 22003 numeric field overflow.
 */
function dimension(raw: string, max: number): { value: number | null; error?: string } {
  const text = raw.trim()
  if (text === '') return { value: null, error: 'Required, in metres.' }
  const value = Number(text)
  if (Number.isNaN(value)) return { value: null, error: 'Enter a number, in metres.' }
  if (value <= 0) return { value: null, error: 'Must be greater than zero.' }
  if (value >= max) return { value: null, error: `Must be less than ${max} m.` }
  return { value }
}

/**
 * Mirrors the constraints in
 * supabase/migrations/20260919090628_create_vessels.sql. The database is still
 * the authority — this only puts a typo in the field it belongs to rather than
 * returning one message about the whole row.
 */
function validate(): VesselInput | null {
  const next: Record<string, string> = {}

  const name = form.value.name.trim()
  if (!name) next.name = 'Enter a name.'

  const company_id = Number(form.value.company_id)
  if (!form.value.company_id || Number.isNaN(company_id)) next.company_id = 'Choose a company.'

  const type = form.value.type
  if (!type) next.type = 'Choose a type.'

  // Ceilings are numeric(6,2), numeric(5,2) and numeric(4,2) respectively.
  const length = dimension(form.value.length_m, 10000)
  if (length.error) next.length_m = length.error

  const width = dimension(form.value.width_m, 1000)
  if (width.error) next.width_m = width.error

  const draft = dimension(form.value.draft_m, 100)
  if (draft.error) next.draft_m = draft.error

  errors.value = next
  if (Object.keys(next).length > 0) return null

  return {
    name,
    company_id,
    type: type as VesselType,
    length_m: length.value as number,
    width_m: width.value as number,
    draft_m: draft.value as number,
  }
}

async function onSubmit(): Promise<void> {
  if (saving.value) return
  const input = validate()
  if (!input) return

  saving.value = true
  formError.value = ''
  try {
    const saved = isEdit.value
      ? await updateVessel(editingId.value as number, input)
      : await createVessel(input)
    // Clear the guard before navigating, or leaving prompts about changes
    // that were just written.
    pristine.value = { ...form.value }
    await router.push(to.vessel(saved.id))
  } catch (e) {
    formError.value = e instanceof DataError ? e.message : 'Could not save the vessel.'
  } finally {
    saving.value = false
  }
}

const cancelTo = computed(() =>
  isEdit.value ? to.vessel(editingId.value as number) : routes.vessels,
)

// Asks before leaving with unsaved edits -- see lib/useUnsavedGuard.ts.
useUnsavedGuard(dirty, saving)
</script>

<template>
  <PageHeader
    :title="isEdit ? `Edit ${heading}` : 'New vessel'"
    description="The ship, and the company that operates it. Dimensions are in metres — length is what berth limits are matched against."
  />

  <p v-if="loading" class="page-status">Loading…</p>

  <Alert v-else-if="notFound" tone="danger">
    That vessel does not exist. <RouterLink :to="routes.vessels">Back to all vessels</RouterLink>.
  </Alert>

  <Panel v-else>
    <form class="form-layout" novalidate @submit.prevent="onSubmit">
      <Alert v-if="formError" tone="danger">{{ formError }}</Alert>

      <Alert v-if="noCompanies" tone="warning">
        There are no companies yet, and every vessel belongs to one.
        <RouterLink :to="routes.companyNew">Add a company</RouterLink> first.
      </Alert>

      <div class="form-grid">
        <TextField
          v-model="form.name"
          label="Name"
          :error="errors.name"
          placeholder="MS Rheinfels"
          autocomplete="off"
          class="form-wide"
          required
        />
        <SelectField
          v-model="form.company_id"
          label="Company"
          placeholder="Select a company"
          :error="
            errors.company_id ??
            (companiesFailed ? 'The company list could not be loaded. Reload the page.' : undefined)
          "
          :disabled="companiesFailed || companies.length === 0"
          hint="The operator of the vessel."
          class="form-wide"
        >
          <option v-for="c in companies" :key="c.id" :value="String(c.id)">{{ c.name }}</option>
        </SelectField>
        <SelectField
          v-model="form.type"
          label="Type"
          placeholder="Select a type"
          :error="errors.type"
        >
          <option v-for="t in vesselTypes" :key="t.code" :value="t.code">{{ t.label }}</option>
        </SelectField>
        <TextField
          v-model="form.length_m"
          label="Length"
          :error="errors.length_m"
          inputmode="decimal"
          hint="Metres, length overall."
          placeholder="110"
          autocomplete="off"
          required
        />
        <TextField
          v-model="form.width_m"
          label="Beam"
          :error="errors.width_m"
          inputmode="decimal"
          hint="Metres, at the widest point."
          placeholder="11.4"
          autocomplete="off"
          required
        />
        <TextField
          v-model="form.draft_m"
          label="Draft"
          :error="errors.draft_m"
          inputmode="decimal"
          hint="Metres, fully laden."
          placeholder="2.5"
          autocomplete="off"
          required
        />
      </div>

      <footer class="form-actions">
        <Button variant="ghost" :to="cancelTo" :disabled="saving">Cancel</Button>
        <Button type="submit" :loading="saving">
          {{ isEdit ? 'Save changes' : 'Create vessel' }}
        </Button>
      </footer>
    </form>
  </Panel>
</template>
